import React, { useMemo } from 'react';
import { computePlaystyleRadar, computeBehaviorTags, computeStrengthsWeaknesses } from '../lib/playerProfile';

/**
 * APEX v8.8 — PlayerProfile
 * Indicateurs joueur façon Porofessor, style néon.
 *   - Radar playstyle SVG (5 axes)
 *   - Tags comportementaux
 *   - Forces / Faiblesses synthétiques
 */

const TAG_STYLES = {
  positive: 'border-cyan-400/40 bg-cyan-400/10 text-cyan-300',
  neutral:  'border-zinc-700 bg-zinc-800/50 text-zinc-400',
  negative: 'border-rose-400/40 bg-rose-400/10 text-rose-300',
};

const RADAR_AXES = [
  { key: 'aggression', label: 'AGRO' },
  { key: 'farming',    label: 'FARM' },
  { key: 'vision',     label: 'VISION' },
  { key: 'survie',     label: 'SURVIE' },
  { key: 'carry',      label: 'CARRY' },
];

function RadarChart({ data }) {
  const size = 220;
  const center = size / 2;
  const radius = 78;
  const n = RADAR_AXES.length;

  // Points du polygone de données
  const points = RADAR_AXES.map((axis, i) => {
    const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
    const val = (data[axis.key] || 0) / 100;
    const r = radius * val;
    return [center + r * Math.cos(angle), center + r * Math.sin(angle)];
  });
  const polygon = points.map(p => p.join(',')).join(' ');

  // Grilles concentriques
  const rings = [0.25, 0.5, 0.75, 1].map(scale =>
    RADAR_AXES.map((_, i) => {
      const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
      const r = radius * scale;
      return [center + r * Math.cos(angle), center + r * Math.sin(angle)].join(',');
    }).join(' ')
  );

  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="w-full max-w-[240px] mx-auto">
      {/* Grilles */}
      {rings.map((ring, i) => (
        <polygon key={i} points={ring} fill="none" stroke="#27272a" strokeWidth="1" />
      ))}
      {/* Axes */}
      {RADAR_AXES.map((axis, i) => {
        const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
        const x = center + radius * Math.cos(angle);
        const y = center + radius * Math.sin(angle);
        const lx = center + (radius + 18) * Math.cos(angle);
        const ly = center + (radius + 18) * Math.sin(angle);
        return (
          <g key={axis.key}>
            <line x1={center} y1={center} x2={x} y2={y} stroke="#27272a" strokeWidth="1" />
            <text x={lx} y={ly} textAnchor="middle" dominantBaseline="middle"
              fill="#71717a" fontSize="9" fontWeight="bold" fontFamily="monospace">
              {axis.label}
            </text>
          </g>
        );
      })}
      {/* Polygone data */}
      <polygon points={polygon} fill="rgba(34,211,238,0.15)" stroke="#22d3ee" strokeWidth="2" />
      {points.map((p, i) => (
        <circle key={i} cx={p[0]} cy={p[1]} r="3" fill="#22d3ee" />
      ))}
    </svg>
  );
}

export default function PlayerProfile({ games }) {
  const radar = useMemo(() => computePlaystyleRadar(games), [games]);
  const tags  = useMemo(() => computeBehaviorTags(games), [games]);
  const { strengths, weaknesses } = useMemo(() => computeStrengthsWeaknesses(games), [games]);

  if (!radar) {
    return (
      <div className="border border-dashed border-zinc-800 p-6 text-center text-zinc-500 text-xs">
        Pas assez de games pour générer ton profil joueur (minimum 5).
      </div>
    );
  }

  return (
    <div className="border border-zinc-800 bg-zinc-950">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-800">

        {/* Radar */}
        <div className="bg-zinc-950 p-5">
          <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase font-semibold mb-3">
            Profil de jeu
          </div>
          <RadarChart data={radar} />
        </div>

        {/* Tags + Forces/Faiblesses */}
        <div className="bg-zinc-950 p-5 space-y-5">
          {/* Tags comportementaux */}
          <div>
            <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase font-semibold mb-2">
              Ton style
            </div>
            <div className="flex flex-wrap gap-1.5">
              {tags.length > 0 ? tags.map((t) => (
                <span key={`${t.type}-${t.label}`} className={`text-[10px] font-bold tracking-wide px-2 py-1 border ${TAG_STYLES[t.type]}`}>
                  {t.label}
                </span>
              )) : (
                <span className="text-xs text-zinc-600">Pas assez de données.</span>
              )}
            </div>
          </div>

          {/* Forces */}
          {strengths.length > 0 && (
            <div>
              <div className="text-[10px] tracking-[0.3em] text-cyan-400 uppercase font-semibold mb-2">
                ▲ Forces
              </div>
              <ul className="space-y-1.5">
                {strengths.map((s) => (
                  <li key={s} className="text-[11px] text-zinc-300 leading-relaxed flex gap-2">
                    <span className="text-cyan-400 shrink-0">·</span> {s}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Faiblesses */}
          {weaknesses.length > 0 && (
            <div>
              <div className="text-[10px] tracking-[0.3em] text-rose-400 uppercase font-semibold mb-2">
                ▼ À travailler
              </div>
              <ul className="space-y-1.5">
                {weaknesses.map((w) => (
                  <li key={w} className="text-[11px] text-zinc-300 leading-relaxed flex gap-2">
                    <span className="text-rose-400 shrink-0">·</span> {w}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
